/**
 * Modern Header Scroll Controller
 * Handles show/hide behavior for header on scroll with advanced state management
 */

class HeaderScrollController {
  constructor(options = {}) {
    // Configuration with defaults (force scrollThreshold=1 for testing)
    this.config = {
      headerSelector: '.site-header',
      scrollThreshold: 1, // FORZADO para pruebas
      hideDelay: 0,
      showDelay: 0,
      disableOnMobile: false,
      mobileBreakpoint: 768,
      ...options
    };
    // Log config for debug
    console.log('[HeaderScrollController] Configuración real:', this.config);

    // State management
    this.state = {
      lastScrollY: 0,
      currentScrollY: 0,
      isScrollingDown: false,
      isHeaderVisible: true,
      isHeaderHidden: false,
      isDragging: false,
      isAtTop: true,
      drawerIsOpen: false,
      megaMenuIsOpen: false,
      isDisabled: false,
      isInitialized: false,
      headerHeight: 0,
      isInitialLoad: true // Nueva propiedad para proteger carga inicial
    };

    // Performance optimization
    this.rafId = null;
    this.timeouts = {
      hide: null,
      show: null,
      debounce: null,
      initialLoad: null // Timeout para protección inicial
    };

    // DOM references
    this.header = null;
    this.body = document.body;

    // Bind methods
    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.updateHeader = this.updateHeader.bind(this);
    this.onDrawerOpen = this.onDrawerOpen.bind(this);
    this.onDrawerClose = this.onDrawerClose.bind(this);
    this.onMegaMenuOpen = this.onMegaMenuOpen.bind(this);
    this.onMegaMenuClose = this.onMegaMenuClose.bind(this);

    this.init();
  }

  init() {
    this.header = document.querySelector(this.config.headerSelector);
    
    if (!this.header) {
      console.warn('HeaderScrollController: Header element not found with selector:', this.config.headerSelector);
      console.log('Available header elements:', document.querySelectorAll('header, .header, .site-header, [class*="header"]'));
      return;
    }

    console.log('HeaderScrollController: Header found:', this.header);
    this.setupInitialState();
    this.bindEvents();
    this.state.isInitialized = true;
    
    // Validate integration
    this.validateIntegration();

    // Emit initialization event
    this.emitEvent('headerScrollInit', { controller: this });
  }

  setupInitialState() {
    this.state.currentScrollY = window.scrollY;
    this.state.lastScrollY = this.state.currentScrollY;
    this.state.isAtTop = this.state.currentScrollY <= this.config.scrollThreshold;
    
    // IMPORTANTE: El header debe estar siempre visible al cargar inicialmente
    this.state.isHeaderVisible = true;
    this.state.isHeaderHidden = false;
    
    // Check actual drawer state from body classes and overlay states
    const mobileMenuOverlay = document.getElementById('mobile-menu-overlay');
    const isMobileMenuOpen = mobileMenuOverlay && mobileMenuOverlay.getAttribute('aria-hidden') === 'false';
    
    this.state.drawerIsOpen = document.body.classList.contains('drawer-open') || 
                              document.body.classList.contains('mobile-menu-open') ||
                              isMobileMenuOpen;
    
    // Set up header height compensation
    this.setupHeaderHeightCompensation();
    
    // Set initial classes - ASEGURAR que esté visible
    this.updateHeaderClasses();
    
    // Forzar estado visible inmediatamente
    if (this.header) {
      this.header.style.transform = 'translateY(0)';
      this.header.classList.add('site-header--force-visible');
    }
    
    // Proteger los primeros 2 segundos para evitar ocultación prematura
    this.timeouts.initialLoad = setTimeout(() => {
      this.state.isInitialLoad = false;
      console.log('HeaderScrollController: Initial load protection disabled');
    }, 2000);
  }

  bindEvents() {
    // Scroll event with passive listener for better performance
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    
    // Resize event
    window.addEventListener('resize', this.handleResize, { passive: true });
    
    // Drawer events
    document.addEventListener('drawerOpen', this.onDrawerOpen);
    document.addEventListener('drawerClose', this.onDrawerClose);
    document.addEventListener('openDrawer', this.onDrawerOpen);
    
    // Mobile menu events
    document.addEventListener('mobileMenuOpen', this.onDrawerOpen);
    document.addEventListener('mobileMenuClose', this.onDrawerClose);

    // Mega menu events (if they exist)
    document.addEventListener('megaMenuOpen', this.onMegaMenuOpen);
    document.addEventListener('megaMenuClose', this.onMegaMenuClose);

    // Body class changes (for drawer states)
    this.observeBodyClasses();
    
    // Header height changes
    this.observeHeaderHeight();
  }

  handleScroll() {
    if (this.rafId) return;
    
    // Debug: Check if scroll handler is being called
    if (window.debugScrollBehavior) {
      console.log('🔄 handleScroll triggered, scrollY:', window.scrollY);
    }
    
    this.rafId = requestAnimationFrame(() => {
      this.updateScrollState();
      this.updateHeader();
      this.rafId = null;
    });
  }

  updateScrollState() {
    this.state.currentScrollY = Math.max(0, window.scrollY);
    
    const scrollDifference = this.state.currentScrollY - this.state.lastScrollY;
    const isScrollingDown = scrollDifference > 0;
    const hasScrolledEnough = Math.abs(scrollDifference) >= this.config.scrollThreshold;
    
    // Debug real scroll behavior
    if (window.debugScrollBehavior && scrollDifference !== 0) {
      console.log('Real scroll detected:', {
        currentScrollY: this.state.currentScrollY,
        lastScrollY: this.state.lastScrollY,
        scrollDifference,
        isScrollingDown,
        hasScrolledEnough,
        threshold: this.config.scrollThreshold
      });
    }
    
    // Always update direction if there's any movement
    if (scrollDifference !== 0) {
      this.state.isScrollingDown = isScrollingDown;
    }
    
    // Update last position only if threshold is met
    if (hasScrolledEnough) {
      this.state.lastScrollY = this.state.currentScrollY;
    }
    
    // Update top state
    this.state.isAtTop = this.state.currentScrollY <= this.config.scrollThreshold;
  }

  updateHeader() {
    const shouldHide = this.shouldHideHeader();
    const shouldShow = this.shouldShowHeader();

    if (shouldHide && this.state.isHeaderVisible) {
      this.hideHeader();
    } else if (shouldShow && !this.state.isHeaderVisible) {
      this.showHeader();
    }
  }

  shouldHideHeader() {
    const result = (
      !this.state.isDisabled &&
      !this.state.isInitialLoad && // Nueva protección
      !this.state.isAtTop &&
      this.state.isScrollingDown &&
      this.state.isHeaderVisible &&
      !this.state.drawerIsOpen &&
      !this.state.megaMenuIsOpen &&
      !this.isMobileAndDisabled()
    );
    
    // Temporary debug logging
    if (window.debugScrollBehavior) {
      console.log('shouldHideHeader check:', {
        result,
        isDisabled: this.state.isDisabled,
        isInitialLoad: this.state.isInitialLoad,
        isAtTop: this.state.isAtTop,
        isScrollingDown: this.state.isScrollingDown,
        isHeaderVisible: this.state.isHeaderVisible,
        drawerIsOpen: this.state.drawerIsOpen,
        megaMenuIsOpen: this.state.megaMenuIsOpen,
        isMobileAndDisabled: this.isMobileAndDisabled()
      });
    }
    
    return result;
  }

  shouldShowHeader() {
    return (
      !this.state.isDisabled &&
      (
        this.state.isAtTop ||
        !this.state.isScrollingDown ||
        this.state.drawerIsOpen ||
        this.state.megaMenuIsOpen
      ) &&
      !this.state.isHeaderVisible
    );
  }

  hideHeader() {
    this.clearTimeouts();
    
    this.timeouts.hide = setTimeout(() => {
      if (this.shouldHideHeader()) {
        this.state.isHeaderVisible = false;
        this.state.isHeaderHidden = true;
        this.updateHeaderClasses();
        this.emitEvent('headerHidden');
      }
    }, this.config.hideDelay);
  }

  showHeader() {
    this.clearTimeouts();
    
    this.timeouts.show = setTimeout(() => {
      this.state.isHeaderVisible = true;
      this.state.isHeaderHidden = false;
      this.updateHeaderClasses();
      this.emitEvent('headerShown');
    }, this.config.showDelay);
  }

  updateHeaderClasses() {
    if (!this.header) return;

    // Remove all scroll-related classes
    this.header.classList.remove(
      'site-header--scrolling-down',
      'site-header--scrolling-up',
      'site-header--hidden',
      'site-header--at-top',
      'site-header--drawer-open',
      'site-header--mega-menu-open',
      'site-header--force-visible'
    );

    // Add current state classes
    if (this.state.isAtTop) {
      this.header.classList.add('site-header--at-top');
    }

    if (this.state.drawerIsOpen) {
      this.header.classList.add('site-header--drawer-open');
    }

    if (this.state.megaMenuIsOpen) {
      this.header.classList.add('site-header--mega-menu-open');
    }

    if (this.state.isHeaderHidden) {
      this.header.classList.add('site-header--scrolling-down', 'site-header--hidden');
    } else {
      this.header.classList.add('site-header--scrolling-up');
    }

    // Force visible for special states
    if (this.state.drawerIsOpen || this.state.megaMenuIsOpen || this.state.isAtTop) {
      this.header.classList.add('site-header--force-visible');
    }
  }

  // Event handlers
  onDrawerOpen() {
    this.state.drawerIsOpen = true;
    this.showHeader();
  }

  onDrawerClose() {
    this.state.drawerIsOpen = false;
    this.updateHeader();
  }

  onMegaMenuOpen() {
    this.state.megaMenuIsOpen = true;
    this.showHeader();
  }

  onMegaMenuClose() {
    this.state.megaMenuIsOpen = false;
    this.updateHeader();
  }

  handleResize() {
    this.clearTimeouts();
    
    this.timeouts.debounce = setTimeout(() => {
      this.setupInitialState();
      // Recalculate header height on resize
      this.setupHeaderHeightCompensation();
    }, 100);
  }

  // Observer for body class changes
  observeBodyClasses() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const hasDrawerOpen = this.body.classList.contains('drawer-open');
          const hasMobileMenuOpen = this.body.classList.contains('mobile-menu-open');
          
          // Also check mobile menu overlay state
          const mobileMenuOverlay = document.getElementById('mobile-menu-overlay');
          const isMobileMenuOverlayOpen = mobileMenuOverlay && mobileMenuOverlay.getAttribute('aria-hidden') === 'false';
          
          const shouldBeOpen = hasDrawerOpen || hasMobileMenuOpen || isMobileMenuOverlayOpen;
          
          if (shouldBeOpen && !this.state.drawerIsOpen) {
            this.onDrawerOpen();
          } else if (!shouldBeOpen && this.state.drawerIsOpen) {
            this.onDrawerClose();
          }
        }
      });
    });

    observer.observe(this.body, { 
      attributes: true, 
      attributeFilter: ['class'] 
    });
    
    // Also observe mobile menu overlay changes
    const mobileMenuOverlay = document.getElementById('mobile-menu-overlay');
    if (mobileMenuOverlay) {
      const overlayObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
            const isOpen = mobileMenuOverlay.getAttribute('aria-hidden') === 'false';
            if (isOpen && !this.state.drawerIsOpen) {
              this.onDrawerOpen();
            } else if (!isOpen && this.state.drawerIsOpen) {
              // Check if any other drawer is still open
              const hasOtherDrawerOpen = this.body.classList.contains('drawer-open');
              if (!hasOtherDrawerOpen) {
                this.onDrawerClose();
              }
            }
          }
        });
      });
      
      overlayObserver.observe(mobileMenuOverlay, {
        attributes: true,
        attributeFilter: ['aria-hidden']
      });
    }
  }

  // Observer for header height changes
  observeHeaderHeight() {
    if (!this.header) return;
    
    // Use ResizeObserver to detect header size changes
    if (window.ResizeObserver) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const newHeight = entry.contentRect.height;
          if (newHeight !== this.state.headerHeight) {
            console.log(`Header height changed from ${this.state.headerHeight}px to ${newHeight}px`);
            this.setupHeaderHeightCompensation();
          }
        }
      });
      
      resizeObserver.observe(this.header);
      
      // Store observer for cleanup
      this.headerResizeObserver = resizeObserver;
    } else {
      // Fallback for browsers without ResizeObserver
      console.warn('ResizeObserver not supported, header height changes may not be detected');
    }
  }

  // Header height compensation
  setupHeaderHeightCompensation() {
    if (!this.header) return;
    
    // Get the actual height of the header
    const headerHeight = this.header.offsetHeight;
    
    // Find main content element (try different selectors)
    const mainElement = document.querySelector('main') || 
                       document.querySelector('.main-content') ||
                       document.querySelector('#main') ||
                       document.querySelector('[role="main"]');
    
    if (!mainElement) {
      console.warn('HeaderScrollController: Main content element not found - cannot apply header height compensation');
      return;
    }
    
    // Set CSS custom property for header height
    document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    
    // Add class to body to indicate dynamic header height is active
    document.body.classList.add('header-height-dynamic');
    
    // Log for debugging
    console.log(`HeaderScrollController: Header height detected: ${headerHeight}px`);
    console.log('HeaderScrollController: Applied dynamic header height compensation');
    
    // Store height in state for reference
    this.state.headerHeight = headerHeight;
  }

  // Utility methods
  isMobileAndDisabled() {
    return this.config.disableOnMobile && window.innerWidth <= this.config.mobileBreakpoint;
  }
  
  validateIntegration() {
    const issues = [];
    
    // Check CSS classes
    if (!this.header.classList.length) {
      issues.push('Header has no CSS classes - check if styles are loaded');
    }
    
    // Check mobile menu overlay
    const mobileMenuOverlay = document.getElementById('mobile-menu-overlay');
    if (!mobileMenuOverlay) {
      issues.push('Mobile menu overlay not found - mobile menu events may not work');
    }
    
    // Check if drawer.js is loaded by looking for drawer events
    const hasDrawerListeners = document.querySelector('[data-drawer]') || 
                              document.querySelector('.js-drawer-open');
    if (!hasDrawerListeners) {
      console.warn('HeaderScrollController: No drawer elements found - drawer events may not work');
    }
    
    if (issues.length > 0) {
      console.warn('HeaderScrollController: Integration issues found:');
      issues.forEach(issue => console.warn('- ' + issue));
    } else {
      console.log('HeaderScrollController: Integration validation passed');
    }
  }

  clearTimeouts() {
    Object.values(this.timeouts).forEach(timeout => {
      if (timeout) clearTimeout(timeout);
    });
    this.timeouts = { hide: null, show: null, debounce: null, initialLoad: null };
  }

  emitEvent(eventName, detail = {}) {
    const event = new CustomEvent(eventName, {
      detail: {
        ...detail,
        scrollY: this.state.currentScrollY,
        isHeaderVisible: this.state.isHeaderVisible,
        state: { ...this.state }
      }
    });
    document.dispatchEvent(event);
  }

  // Public API
  enable() {
    this.state.isDisabled = false;
    this.updateHeader();
  }

  disable() {
    this.state.isDisabled = true;
    this.showHeader();
  }

  forceShow() {
    this.clearTimeouts();
    this.showHeader();
  }

  forceHide() {
    if (!this.state.drawerIsOpen && !this.state.megaMenuIsOpen && !this.state.isAtTop) {
      this.clearTimeouts();
      this.hideHeader();
    }
  }

  getState() {
    return { ...this.state };
  }

  // Recalculate header height (useful for dynamic content changes)
  recalculateHeaderHeight() {
    this.setupHeaderHeightCompensation();
    console.log('Header height recalculated:', this.state.headerHeight + 'px');
  }

  // Debug method - call from browser console
  debug() {
    console.log('=== Header Scroll Controller Debug ===');
    console.log('Header element:', this.header);
    console.log('Header classes:', this.header ? Array.from(this.header.classList) : 'No header');
    console.log('Header height:', this.state.headerHeight + 'px');
    console.log('CSS --header-height:', getComputedStyle(document.documentElement).getPropertyValue('--header-height'));
    console.log('Config:', this.config);
    console.log('State:', this.state);
    console.log('Current scroll position:', window.scrollY);
    console.log('Should hide header:', this.shouldHideHeader());
    console.log('Should show header:', this.shouldShowHeader());
    
    // Validate integration
    console.log('\n--- Integration Check ---');
    const mobileMenuOverlay = document.getElementById('mobile-menu-overlay');
    console.log('Mobile menu overlay found:', !!mobileMenuOverlay);
    if (mobileMenuOverlay) {
      console.log('Mobile menu state (aria-hidden):', mobileMenuOverlay.getAttribute('aria-hidden'));
    }
    console.log('Body classes:', Array.from(document.body.classList));
    console.log('Drawer state detected:', this.state.drawerIsOpen);
    
    // Check main element compensation
    const mainElement = document.querySelector('main');
    if (mainElement) {
      const mainStyles = getComputedStyle(mainElement);
      console.log('Main element padding-top:', mainStyles.paddingTop);
      console.log('Main element margin-top:', mainStyles.marginTop);
    } else {
      console.warn('Main element not found');
    }
    
    // Test scroll detection with direct manipulation
    const originalScrollY = this.state.currentScrollY;
    const originalLastScrollY = this.state.lastScrollY;
    const originalIsScrollingDown = this.state.isScrollingDown;
    const originalIsAtTop = this.state.isAtTop;
    
    console.log('\n--- Scroll Test ---');
    console.log('Test: Simulating scroll down from current position...');
    
    // Simulate scroll down by 50px
    this.state.lastScrollY = originalScrollY;
    this.state.currentScrollY = originalScrollY + 50;
    this.state.isScrollingDown = true;
    this.state.isAtTop = false;
    
    console.log('Simulated state:', {
      lastScrollY: this.state.lastScrollY,
      currentScrollY: this.state.currentScrollY,
      isScrollingDown: this.state.isScrollingDown,
      isAtTop: this.state.isAtTop
    });
    console.log('Would hide with simulated scroll:', this.shouldHideHeader());
    
    // Reset to original state
    this.state.currentScrollY = originalScrollY;
    this.state.lastScrollY = originalLastScrollY;
    this.state.isScrollingDown = originalIsScrollingDown;
    this.state.isAtTop = originalIsAtTop;
    
    console.log('State restored to original');
  }

  // Force reset state
  resetState() {
    console.log('Resetting header scroll state...');
    this.state.drawerIsOpen = false;
    this.state.megaMenuIsOpen = false;
    this.state.isHeaderVisible = true;
    this.state.isHeaderHidden = false;
    this.updateHeaderClasses();
    console.log('State reset complete');
  }

  // Test scroll behavior
  testScrollDown() {
    console.log('=== Testing Scroll Down Behavior ===');
    
    // Force conditions for hiding
    this.state.isScrollingDown = true;
    this.state.isAtTop = false;
    this.state.isHeaderVisible = true;
    this.state.drawerIsOpen = false;
    this.state.megaMenuIsOpen = false;
    this.state.isDisabled = false;
    
    console.log('Forced state for test:', this.state);
    console.log('Should hide now:', this.shouldHideHeader());
    
    if (this.shouldHideHeader()) {
      console.log('✅ Test passed - triggering hide...');
      this.hideHeader();
      setTimeout(() => {
        console.log('Header classes after hide:', Array.from(this.header.classList));
      }, 100);
    } else {
      console.log('❌ Test failed - conditions not met');
    }
  }

  destroy() {
    this.clearTimeouts();
    
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    
    // Clean up resize observer
    if (this.headerResizeObserver) {
      this.headerResizeObserver.disconnect();
    }

    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('drawerOpen', this.onDrawerOpen);
    document.removeEventListener('drawerClose', this.onDrawerClose);
    document.removeEventListener('openDrawer', this.onDrawerOpen);
    document.removeEventListener('mobileMenuOpen', this.onDrawerOpen);
    document.removeEventListener('mobileMenuClose', this.onDrawerClose);
    document.removeEventListener('megaMenuOpen', this.onMegaMenuOpen);
    document.removeEventListener('megaMenuClose', this.onMegaMenuClose);

    this.emitEvent('headerScrollDestroy');
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Initialize with default configuration
  window.headerScrollController = new HeaderScrollController({
    scrollThreshold: 1, // Even lower threshold - detects any scroll movement
    hideDelay: 0,       // Immediate response
    showDelay: 0
  });
  
  // Expose debug function globally
  window.debugHeaderScroll = () => window.headerScrollController.debug();
  
  // Expose manual control functions
  window.hideHeader = () => window.headerScrollController.forceHide();
  window.showHeader = () => window.headerScrollController.forceShow();
  window.resetHeaderState = () => window.headerScrollController.resetState();
  window.recalculateHeaderHeight = () => window.headerScrollController.recalculateHeaderHeight();
  
  // Enable detailed debugging
  window.enableScrollDebug = () => { window.debugScrollBehavior = true; };
  window.disableScrollDebug = () => { window.debugScrollBehavior = false; };
  
  console.log('Header Scroll Controller initialized.');
  console.log('Available debug commands:');
  console.log('- debugHeaderScroll() - Show debug info');
  console.log('- hideHeader() - Manually hide header');
  console.log('- showHeader() - Manually show header');
  console.log('- resetHeaderState() - Reset drawer/menu states');
  console.log('- recalculateHeaderHeight() - Recalculate header height');
  console.log('- enableScrollDebug() - Enable real-time debug logs');
  console.log('- disableScrollDebug() - Disable real-time debug logs');
});

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = HeaderScrollController;
}
